import { createContext } from 'preact';
import { useContext, useEffect } from 'preact/hooks';

/**
 * Register a modal of your own as covering the flow. Returns the function that unregisters it.
 *
 * Null outside the flow — the product cards also render on their own, where there is nothing to
 * hold open.
 */
type RegisterCoveringModal = () => () => void;

export const CoveringModalContext = createContext<RegisterCoveringModal | null>(null);

/**
 * Hold the flow open while this component has a modal of its own on screen.
 *
 * A grommet `Layer` treats a click on *any* backdrop as a click outside itself, including the
 * backdrop of a layer stacked above it: the portal id it checks against sits on the layer's content
 * container, and the backdrop is that container's sibling, so a backdrop click resolves to no portal
 * at all and every mounted layer's document listener accepts it. Dismissing the flow is therefore
 * something only the topmost modal can be allowed to decide, and while one is open the flow has to
 * stop listening.
 *
 * Counted rather than a boolean so two overlapping modals cannot have the first to close re-enable
 * dismissal underneath the second.
 */
export const useCoveringModal = (open: boolean) => {
  const register = useContext(CoveringModalContext);
  useEffect(() => {
    if (!register || !open) return undefined;
    return register();
  }, [register, open]);
};
